import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAdmissionRequirements, getEnrollmentRequirements, getExamDetailsData, getExamInterviewData, getPersonalDetails, getUserData, getVoucherApplication, logoutUser, updateEnrollmentApprovalStatus, verifyUser } from "../api/applicant";
import axios from "axios";

export const useEnrollmentStatus = () => {
    const navigate = useNavigate();

    // User data state
    const [userData, setUserData] = useState({});

    // Application status states
    const [applicationStatus, setApplicationStatus] = useState({
        registrationStatus: "Incomplete",
        admissionRequirementsStatus: "Incomplete",
        admissionAdminFirstStatus: "On-going",
        preferredExamAndInterviewApplicationStatus: "Incomplete",
        admissionExamDetailsStatus: "Incomplete",
        approvedExamFeeStatus: "Required",
        approvedExamInterviewResult: "Pending",
        examInterviewResultStatus: "Incomplete",
        reservationFeePaymentStepStatus: "Incomplete",
        admissionApprovalStatus: "Incomplete",
        enrollmentRequirementsStatus: "Incomplete",
        voucherApplicationStatus: "Incomplete",
        enrollmentApprovalAdminStatus: "Pending",
        enrollmentApprovalStatus: "Incomplete",
        reservationFeeAmountPaid: 0
    });

    // UI states
    const [loading, setLoading] = useState(true);
    const [loadingInitial, setLoadingInitial] = useState(true);
    const [error, setError] = useState("");

    const updateApplicationStatus = (key, value) => {
        setApplicationStatus((prev) => ({ ...prev, [key]: value }));
    };

    const handleLogout = async () => {
        try {
            const userEmail = localStorage.getItem("userEmail");
            const createdAt = localStorage.getItem("createdAt");

            if (!userEmail) {
                navigate("/scope-login", {
                    state: { error: "No active session found." },
                });
                return;
            }

            await logoutUser(userEmail, createdAt);
            localStorage.clear(); // force full cleanup
            sessionStorage.clear(); // optional: in case session storage was used
       
            navigate("/scope-login");
        } catch (err) {
            setError("Error during logout process. Please check your connection.");
        }
    };

    const checkSession = () => {
        const userEmail = localStorage.getItem("userEmail");
        const createdAt = localStorage.getItem("createdAt");

        if (!userEmail || !createdAt) {
            navigate("/scope-login", {
                state: { error: "No active session found. Please log in." },
            });
            return false;
        }

        const createdAtDate = new Date(createdAt);
        if (isNaN(createdAtDate.getTime())) {
            handleLogout();
            navigate("/scope-login", {
                state: {
                    accountInactive: true,
                    error: "Invalid session data. Please log in again.",
                },
            });
            return false;
        }

        return { userEmail, createdAt };
    };

    const fetchAllUserData = async () => {
        const sessionData = checkSession();
        if (!sessionData) return;

        const { userEmail, createdAt } = sessionData;

        try {
            // Only set loading to true on initial load
            if (loadingInitial) {
                setLoading(true);
            }
            setError("");

            // Verify user status
            const verificationData = await verifyUser(userEmail);

            if (
                verificationData.status !== "Active" ||
                Math.abs(
                    new Date(verificationData.createdAt).getTime() -
                    new Date(createdAt).getTime()
                ) > 1000
            ) {
                handleLogout();
                navigate("/scope-login", {
                    state: {
                        accountInactive: true,
                        error: "Account is inactive or session expired.",
                    },
                });
                return;
            }

            // Fetch user data and essential information first
            const [userDataResponse, applicantData] = await Promise.all([
                getUserData(userEmail, createdAt),
                getPersonalDetails(userEmail)
            ]);

            console.log("Fetched applicantData:", {
                email: applicantData.emailAddress || userEmail,
                preferredSection: applicantData.preferredSection || 'Not set in personal-details',
            }); // Debug log

            // Fetch preferredSection separately if not included
            let preferredSection = applicantData.preferredSection;
            if (!preferredSection || preferredSection === 'Not assigned') {
                console.warn(`preferredSection missing for ${userEmail}, fetching from /preferred-section`);
                try {
                    const sectionResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/preferred-section/${userEmail}`);
                    preferredSection = sectionResponse.data.preferredSection;
                    console.log(`Fetched preferredSection: ${preferredSection}`);
                } catch (sectionErr) {
                    console.error('Error fetching preferredSection:', sectionErr.message);
                    preferredSection = 'Not yet assigned';
                }
            }

            // Store data in localStorage
            localStorage.setItem(
                "applicantID",
                applicantData.applicantID || userDataResponse.applicantID || ""
            );
            localStorage.setItem(
                "firstName",
                applicantData.firstName || userDataResponse.firstName || ""
            );
            localStorage.setItem("middleName", applicantData.middleName || "");
            localStorage.setItem(
                "lastName",
                applicantData.lastName || userDataResponse.lastName || ""
            );
            localStorage.setItem(
                "dob",
                applicantData.dob
                    ? new Date(applicantData.dob).toISOString().split("T")[0]
                    : ""
            );
            localStorage.setItem("nationality", applicantData.nationality || "");

            // Update user data state
            setUserData({
                email: userEmail,
                ...applicantData,
                preferredSection // Ensure preferredSection is included
            });

            updateApplicationStatus(
                "registrationStatus",
                applicantData.registrationStatus || "Incomplete"
            );
            updateApplicationStatus(
                "admissionAdminFirstStatus",
                applicantData.admissionAdminFirstStatus || "On-going"
            );

            updateApplicationStatus(
                "reservationFeeAmountPaid",
                applicantData.reservationFeeAmountPaid || 0
            );

            // Fetch remaining data in parallel
            const [
                admissionData,
                examInterviewData,
                examDetailsData,
                enrollmentData,
                voucherData
            ] = await Promise.all([
                getAdmissionRequirements(userEmail).catch(err => {
                    console.error("Failed to fetch admission requirements:", err.message);
                    return {};
                }),
                getExamInterviewData(userEmail).catch(err => {
                    console.error("Failed to fetch exam interview data:", err.message);
                    return {};
                }),
                getExamDetailsData(userEmail).catch(err => {
                    console.error("Failed to fetch exam details:", err.message);
                    return {};
                }),
                getEnrollmentRequirements(userEmail).catch(err => {
                    console.error("Failed to fetch enrollment requirements:", err.message);
                    return {};
                }),
                getVoucherApplication(userEmail).catch(err => {
                    console.error("Failed to fetch voucher application:", err.message);
                    return {};
                })
            ]);

            // Update application status from all fetched data
            updateApplicationStatus(
                "admissionRequirementsStatus",
                admissionData.admissionRequirementsStatus || "Incomplete"
            );
            updateApplicationStatus(
                "admissionAdminFirstStatus",
                admissionData.admissionAdminFirstStatus || "On-going"
            );
            
            updateApplicationStatus(
                "preferredExamAndInterviewApplicationStatus",
                examInterviewData.preferredExamAndInterviewApplicationStatus ||
                "Incomplete"
            );
            
            updateApplicationStatus(
                "admissionExamDetailsStatus",
                examDetailsData.admissionExamDetailsStatus || "Incomplete"
            );
            updateApplicationStatus(
                "approvedExamFeeStatus",
                examDetailsData.approvedExamFeeStatus || "Required"
            );
            updateApplicationStatus(
                "approvedExamInterviewResult",
                examDetailsData.approvedExamInterviewResult || "Pending"
            );
            updateApplicationStatus(
                "examInterviewResultStatus",
                examDetailsData.examInterviewResultStatus || "Incomplete"
            );
            updateApplicationStatus(
                "reservationFeePaymentStepStatus",
                examDetailsData.reservationFeePaymentStepStatus || "Incomplete"
            );
            updateApplicationStatus(
                "admissionApprovalStatus",
                examDetailsData.admissionApprovalStatus || "Incomplete"
            );
            
            updateApplicationStatus(
                "enrollmentRequirementsStatus",
                enrollmentData.enrollmentRequirementsStatus || "Incomplete"
            );
            
            updateApplicationStatus(
                "voucherApplicationStatus",
                voucherData.voucherApplicationStatus || "Incomplete"
            );
            updateApplicationStatus(
                "enrollmentApprovalAdminStatus",
                voucherData.enrollmentApprovalAdminStatus || "Pending"
            );
            updateApplicationStatus(
                "enrollmentApprovalStatus",
                voucherData.enrollmentApprovalStatus || "Incomplete"
            );

            setUserData((prev) => ({
                ...prev,
                enrollmentApprovalRejectMessage:
                    voucherData.enrollmentApprovalRejectMessage || "",
                preferredSection // Ensure preferredSection persists
            }));

            // Check if enrollment approval should be updated
            if (
                voucherData.enrollmentApprovalAdminStatus === "Approved" &&
                voucherData.enrollmentApprovalStatus !== "Complete"
            ) {
                await updateEnrollmentApprovalStatus(userEmail, "Complete");
                updateApplicationStatus("enrollmentApprovalStatus", "Complete");
            }
        } catch (err) {
            console.error("Fetch data error:", err.message);
            setError(
                "Failed to load user data. Please check your connection and try again."
            );
        } finally {
            setLoading(false);
            setLoadingInitial(false);
        }
    };

    // Initial data load
    useEffect(() => {
        fetchAllUserData();
        const refreshInterval = setInterval(() => {
            // Regular updates do not need to show loading indicator
            fetchAllUserData();
        }, 2 * 60 * 1000); // Every 2 minutes

        return () => clearInterval(refreshInterval);
    }, []);

    return {
        userData,
        applicationStatus,
        loading,
        error,
        handleLogout,
        fetchAllUserData,
    };
};

export default useEnrollmentStatus;